<template>
  <div>
    <b-container class="wide">
      <div class="d-flex" v-if="selectedProductSheet">
        <head-block :title="$t('title.customer.product_sheet_detail', {productSheetName: selectedProductSheet.name})" />
        <b-button
          size="sm"
          style="max-height: 30px;"
          class="ml-2 align-self-center"
          v-b-tooltip.hover
          :title="$t('table.product_sheets.button_edit')"
          variant="primary"
          @click="editProductSheet()">
          <b-icon icon="pencil-square"></b-icon>
        </b-button>
      </div>
      <div class="mb-3 mt-3">
        <b-button
          variant="outline-primary"
          :to="{ name: 'product-sheets' }"
          v-html="$t('links.user.go_back')"
        >
        </b-button>
      </div>
      <div class="d-flex" v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
        <div>
          <b-button
            class="mb-2 mt-2 mr-3"
            variant="success"
            @click="checkProductsValidity">
            {{ $t('table.products.button_save') }}
          </b-button>
        </div>

        <div>
          <b-button
            v-if="$refs.bulkCreateProductModal && $refs.bulkCreateProductModal.maxAmount > 0"
            class="mb-2 mt-2"
            variant="primary"
            @click="$bvModal.show('bulk-create-product')">
            {{ $t('table.products.button_bulk_create') }}
          </b-button>
        </div>

      </div>
      <content-table
        :fields="fields"
        :items="productItems"
        :slots="templates"
        :primary-key="primaryKey"
        head-variant="dark"
      >
        <template slot="name" slot-scope="data">
          <product-sheet-cell-input
            v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)"
            field="name"
            :value="data.tbl.item.name"
            :isFlexposMarket="isFlexposMarket"
            @cellUpdated="updateProductCell(data.tbl.item, 'name', $event)"
          />
          <span v-else>{{ data.tbl.item.name }}</span>
        </template>
        <template slot="price" slot-scope="data">
          <product-sheet-cell-input
            v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)"
            field="originalPrice"
            :value="data.tbl.item.originalPrice"
            :isFlexposMarket="isFlexposMarket"
            @cellUpdated="updateProductCell(data.tbl.item, 'originalPrice', $event)"
          />
          <span v-else>{{ data.tbl.item.originalPrice }}</span>
        </template>
        <template slot="description" slot-scope="data">
          <product-sheet-cell-input
            v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)"
            field="description"
            :value="data.tbl.item.description"
            :isFlexposMarket="isFlexposMarket"
            @cellUpdated="updateProductCell(data.tbl.item, 'description', $event)"
          />
          <span v-else>{{ data.tbl.item.description }}</span>
        </template>
        <template slot="barcode" slot-scope="data">
          <product-sheet-cell-input
            v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)"
            field="barcode"
            :value="data.tbl.item.barcode"
            :isFlexposMarket="isFlexposMarket"
            @cellUpdated="updateProductCell(data.tbl.item, 'barcode', $event)"
          />
          <span v-else>{{ data.tbl.item.barcode }}</span>
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            v-if="soldStatuses.includes(data.tbl.item.status) || data.tbl.item.hasSaleLines"
            size="sm"
            v-b-tooltip.hover
            :title="$t('table.products.button_view_product_sales')"
            variant="info"
            :to="{name: 'product-sheet-product-sale-lines', params: { productSheetId: selectedProductSheet.id, productId: data.tbl.item.id }}">
            <b-icon icon="view-stacked"></b-icon>
          </b-button>
        </template>
        <template slot="status" slot-scope="data">
          <div class="d-flex justify-content-center">
            <span v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
              {{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}
              {{ data.tbl.item.status === statusDraft && (data.tbl.item.name && data.tbl.item.originalPrice) ? ` - ${$t('table.products.status_product_info_filled_in')}` : '' }}
              {{ (!data.tbl.item.name || !data.tbl.item.originalPrice) ? ` - ${$t('table.products.status_product_info_not_filled_in')}` : '' }}
            </span>
            <span v-else>{{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}</span>
          </div>
        </template>
      </content-table>
      <bulk-create-product-modal
        modalId="bulk-create-product"
        :productsList="productItems"
        ref="bulkCreateProductModal"
        @productsGenerated="updateProductItems"
      />
      <confirm-item-modal
        modalId="save-products"
        :okButtonTitle="$t('modal.general.ok_button')"
        :cancelButtonTitle="$t('modal.general.cancel_button')"
        :title="$t('modal.products.title_save')"
        :text="$t('modal.products.text_save')"
        @onConfirm="saveProducts"/>
      <edit-product-sheet-modal
        modalId="edit-product-sheet"
        v-if="productSheetObj"
        :selectedObject="productSheetObj"/>
      <div>
        <b-button
          class="mb-2 mt-2 mr-3"
          variant="success"
          @click="checkProductsValidity">
          {{ $t('table.products.button_save') }}
        </b-button>
      </div>
    </b-container>
  </div>
</template>

<script>

  import { mapState } from 'vuex'
  import ContentTable from '@base/components/ContentTable'
  import ProductSheetCellInput from '@base/components/ProductSheetCellInput.vue'
  import Formatting from '@base/mixins/Formatting.vue'
  import BulkCreateProductModal from '@user/components/BulkCreateProductModal.vue'
  import EditProductSheetModal from '@user/components/EditProductSheetModal.vue'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import ConfirmItemModal from '@base/components/ConfirmItemModal.vue'
  import ObjectUtils from '@base/mixins/ObjectUtils.vue'
  import {
    EDITABLE_STATUSES,
    SOLD_STATUSES,
    PRODUCT_DRAFT,
    PRODUCT_STATUSES,
  } from '@base/configs'
  import { USED_PREFIX } from "@base/configs";

  export default {
    name: 'CustomerProductSheetDetailView',

    components: {
      EditProductSheetModal,
      ConfirmItemModal,
      HeadBlock,
      BulkCreateProductModal,
      ContentTable,
      ProductSheetCellInput,
    },

    mixins: [ Formatting, ObjectUtils ],

    data() {
      return {
        primaryKey: 'number',
        sortBy: 'number',
        sortDesc: false,
        templates: [
          {name: 'name', field: 'name'},
          {name: 'price', field: 'originalPrice'},
          {name: 'description', field: 'description'},
          {name: 'barcode', field: 'barcode'},
          {name: 'status', field: 'status'},
          { name: 'actions', field: 'actions' },
        ],
        productSheetObj: null,
        loading: false,
        productItems: [],
        productStatuses: PRODUCT_STATUSES,
        editableStatuses: EDITABLE_STATUSES,
        soldStatuses: SOLD_STATUSES,
        statusDraft: PRODUCT_DRAFT,
      }
    },

    created() {
      this.getProductSheet()
    },

    computed: {
      ...mapState(['selectedProductSheet', 'userMarket']),

      barcodeVisible () {
        return Boolean(this.userMarket && this.userMarket.customerBarcodeVisible)
      },

      isFlexposMarket () {
        return Boolean(
          this.userMarket
          && !this.userMarket.zettleIntegration
          && !this.userMarket.isFlealoverSoloFeature
        )
      },

      genericProductName () {
        if (this.userMarket && this.userMarket.supportCountry && this.selectedProductSheet) {
          if (this.selectedProductSheet.productType === USED_PREFIX) {
            return this.availableCountriesList.find(
                obj => obj.code === this.userMarket.supportCountry.code
            ).genericProductNameUsed
          } else {
            return this.availableCountriesList.find(
                obj => obj.code === this.userMarket.supportCountry.code
            ).genericProductNameNew
          }
        }
        return ''
      },

      fields () {
        const fieldsArray = [
          {key: 'number', label: this.$t('table.products.number'), class: 'text-center number-thead-width'},
          {key: 'name', label: this.$t('table.products.name'), class: 'text-center name-thead-width'},
          {key: 'originalPrice', label: this.$t('table.products.price'), class: 'text-center price-thead-width'},
          {key: 'description', label: this.$t('table.products.description'), class: 'text-center'},
          ...(this.barcodeVisible ? [{key: 'barcode', label: this.$t('table.products.barcode'), class: 'text-center'}] : []),
          {key: 'status', label: this.$t('table.products.status'), class: 'text-center'},
        ]
        // Show actions column only for active products
        if (this.selectedProductSheet && !this.editableStatuses.includes(this.selectedProductSheet.status)) {
          fieldsArray.splice(
            3,
            0,
          {key: 'discountPercent', label: this.$t('table.products.discount'), class: 'text-center discount-thead-width'},
          )
          fieldsArray.splice(
            7,
            0,
            { key: 'actions', label: this.$t('form.general.actions'), class: 'text-center' },
          )
        }
        return fieldsArray
      }
    },

    methods: {

      getProductSheet() {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          this.$store.commit(this.$_mutationTypes.SET_PRODUCT_SHEET, null)
          this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_SHEET, this.$route.params.productSheetId)
            .then(() => {
              this.setInitialProducts()
            })
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      updateProductCell (item, field, { value, invalid }) {
        item[field] = value
        const cellVariants = Object.assign({}, item['_cellVariants'])
        if (invalid)
          cellVariants[field] = 'danger'
        else
          delete cellVariants[field]
        item['_cellVariants'] = cellVariants
        this.$refs.bulkCreateProductModal.updateMaxAmount(this.productItems)
      },

      editProductSheet () {
        this.productSheetObj = this.$_.cloneDeep(this.selectedProductSheet)
        this.$nextTick(() => {
          this.$bvModal.show('edit-product-sheet')
        })
      },

      setInitialProducts () {
        // Reset products list
        this.productItems = []
        this.productItems['_cellVariants'] = {}

        for (let number = this.selectedProductSheet.productIdFrom; number <= this.selectedProductSheet.productIdTo; number++) {
          const newProductObj = {
            productSheet: this.$route.params.productSheetId,
            id: null,
            number: number,
            name: null,
            originalPrice: null,
            discountPercent: null,
            description: null,
            barcode: null,
            status: this.statusDraft,
            _cellVariants: {},
          }
          if (this.selectedProductSheet.products.length) {
            const existingProductObj = this.selectedProductSheet.products.find(obj => obj.sheetProductId === number)
            if (existingProductObj) {
              newProductObj.id = existingProductObj.id
              newProductObj.name = existingProductObj.name
              newProductObj.originalPrice = existingProductObj.originalPrice
              newProductObj.discountPercent = existingProductObj.discountPercent
              newProductObj.status = existingProductObj.status
              newProductObj.description = existingProductObj.description
              newProductObj.barcode = existingProductObj.barcode
            }
          }
          this.productItems.push(newProductObj)
        }
        this.$refs.bulkCreateProductModal.updateMaxAmount(this.productItems)
      },

      updateProductItems (productList) {
        this.productItems = this.$_.cloneDeep(productList)
      },

      checkProductsValidity () {
        if (this.productItems.filter(product => !product.name || !product.originalPrice).length) {
          this.$bvModal.show('save-products')
        } else {
          this.saveProducts()
        }
      },

      saveProducts () {
        if (this.loading)
          return
        for (const [index, product] of this.productItems.entries()) {
          if (Object.keys(product['_cellVariants']).length) {
            this.$notify({
              group: 'app',
              type: 'warn',
              title: this.$t('notifications.title.warning'),
              text: this.$t('notifications.text.products.warning_correct_errors')
            })
            return
          }
          this.productItems[index].name = this.productItems[index].name ? this.productItems[index].name : null
          this.productItems[index].originalPrice = this.productItems[index].originalPrice ? this.productItems[index].originalPrice : null
          this.productItems[index].description = this.productItems[index].description ? this.productItems[index].description : null
          this.productItems[index].barcode = this.productItems[index].barcode ? this.productItems[index].barcode : null
        }

        // Exclude blank products from saving
        const productItemsToSave = this.productItems.filter(obj => obj.name || obj.originalPrice)

        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.dispatch(this.$_actionTypes.CREATE_USER_PRODUCTS, { productSheetId: this.selectedProductSheet.id, products: productItemsToSave })
          .then(() => {
            this.$nextTick(() => {
              this.$router.push({name: 'product-sheets'})
            })
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('notifications.title.success'),
              text: this.$t('notifications.text.products.success_products_created')
            })
          })
          .catch((error) => {
            this.$_notifyError(error, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      }
    }
  }

</script>

<style>

  .number-thead-width {
    min-width: 50px;
    max-width: 50px;
  }
  .name-thead-width {
    max-width: 250px;
  }
  .price-thead-width {
    max-width: 200px;
  }
  .discount-thead-width {
    max-width: 50px;
  }

</style>
